import { canvassesGateway } from "@/gateways/canvassesGateway";
import type { MutationCallbacks } from "@/types";
import type { User } from "@/users/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, toValue, type MaybeRefOrGetter } from "vue";
import type { Canvass, CanvassParticipationState } from "./types";

const POLL_INTERVAL_MS = 3_000;

export function useNewCanvass() {
  const canvass = computed<Canvass>(() => ({
    id: window.crypto.randomUUID(),
    name: "",
    meetingPoint: {
      description: "",
      time: null,
      coordinates: null,
    },
    conversationGuide: null,
    abstract: null,
    area: { id: "", name: "", coordinates: [] },
    canvassTargetsWithProgress: [],
    turfCount: 1,
    participantIds: [],
    createdAt: new Date().toISOString(),
    shortUrlId: "",
    public: false,
  }));

  return { canvass };
}

export function useGetAllCanvasses(
  houseId?: number,
  { enablePolling = false } = {},
) {
  const query = useQuery({
    queryKey: ["canvasses", houseId],
    queryFn: () => canvassesGateway.listCanvasses(houseId),
    refetchInterval: enablePolling ? POLL_INTERVAL_MS : false,
  });

  return {
    ...query,
    getCanvassesStatus: query.status,
    canvasses: query.data,
  };
}

export function useGetCanvass(id: string) {
  const query = useQuery({
    queryKey: ["canvass", id],
    queryFn: () => canvassesGateway.getCanvass(id),
  });

  return {
    ...query,
    getCanvassStatus: query.status,
    canvass: query.data,
  };
}

export function useUpdateCanvass() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (canvass: Canvass) => canvassesGateway.updateCanvass(canvass),
    onSuccess: (canvass: Canvass) => {
      queryClient.invalidateQueries({ queryKey: ["canvass", canvass.id] });
      queryClient.invalidateQueries({ queryKey: ["canvasses"] });
      queryClient.invalidateQueries({ queryKey: ["canvassStats", canvass.id] });
      queryClient.setQueryData(["canvass", canvass.id], canvass);
    },
  });

  return {
    ...mutation,
    updateCanvassStatus: mutation.status,
    resetUpdateCanvass: mutation.reset,
    updateCanvass: mutation.mutate,
  };
}

export function useCreateCanvass(callbacks: MutationCallbacks<Canvass> = {}) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (canvass: Canvass) => canvassesGateway.createCanvass(canvass),
    onSuccess: (canvass: Canvass) => {
      queryClient.invalidateQueries({ queryKey: ["canvasses"] });
      queryClient.setQueryData(["canvass", canvass.id], canvass);

      if (callbacks.onSuccess) callbacks.onSuccess(canvass);
    },
  });

  return {
    ...mutation,
    createCanvassStatus: mutation.status,
    createCanvass: mutation.mutate,
  };
}

export function useDeleteCanvass() {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (canvassId: string) =>
      canvassesGateway.deleteCanvass(canvassId),
    onSuccess: (_, canvassId: string) => {
      queryClient.invalidateQueries({ queryKey: ["canvasses"] });
      queryClient.invalidateQueries({ queryKey: ["canvass", canvassId] });
    },
  });

  return {
    ...mutation,
    resetDeleteCanvass: mutation.reset,
    deleteCanvassStatus: mutation.status,
    deleteCanvass: mutation.mutate,
  };
}

export function useGetCanvassStats(id: string) {
  const query = useQuery({
    queryKey: ["canvassStats", id],
    queryFn: () => canvassesGateway.getCanvassStats(id),
  });

  return {
    ...query,
    getCanvassStatsStatus: query.status,
    canvassStats: query.data,
  };
}

export function usePublishCanvass(id: string) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => canvassesGateway.publishCanvass(id),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["canvasses"] });
      queryClient.invalidateQueries({ queryKey: ["canvassStats", id] });
      queryClient.invalidateQueries({ queryKey: ["canvass", id] });
    },
  });

  return {
    ...mutation,
    publishCanvassStatus: mutation.status,
    publishCanvass: mutation.mutate,
  };
}

export function useUnpublishCanvass(id: string) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => canvassesGateway.unpublishCanvass(id),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["canvasses"] });
      queryClient.invalidateQueries({ queryKey: ["canvassStats", id] });
      queryClient.invalidateQueries({ queryKey: ["canvass", id] });
    },
  });

  return {
    ...mutation,
    unpublishCanvassStatus: mutation.status,
    unpublishCanvass: mutation.mutate,
  };
}

export function useAddParticipant(canvass: MaybeRefOrGetter<Canvass>) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (user: User) =>
      canvassesGateway.addParticipant(toValue(canvass), user),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["canvass", toValue(canvass).id],
      });
      queryClient.invalidateQueries({ queryKey: ["canvasses"] });
    },
  });

  return {
    ...mutation,
    addParticipantStatus: mutation.status,
    resetAddParticipant: mutation.reset,
    addParticipant: mutation.mutate,
  };
}

export function useDeleteParticipant(canvass: MaybeRefOrGetter<Canvass>) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (user: User) =>
      canvassesGateway.deleteParticipant(toValue(canvass), user),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["canvass", toValue(canvass).id],
      });
      queryClient.invalidateQueries({ queryKey: ["canvasses"] });
    },
  });

  return {
    ...mutation,
    deleteParticipantStatus: mutation.status,
    resetDeleteParticipant: mutation.reset,
    deleteParticipant: mutation.mutate,
  };
}

export function useParticipateInCanvass(
  canvassId: string,
  callbacks: MutationCallbacks<CanvassParticipationState> & {
    onSuccess?: (participationState: CanvassParticipationState) => void;
  } = {},
) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      return await canvassesGateway.participateInCanvass(canvassId);
    },
    onSuccess: (result: { participationState: CanvassParticipationState }) => {
      queryClient.invalidateQueries({ queryKey: ["canvasses"] });
      callbacks.onSuccess?.(result.participationState);
    },
    onError(error) {
      callbacks.onError?.(error);
    },
    onSettled() {
      callbacks.onSettled?.();
    },
  });

  return {
    ...mutation,
    participateInCanvassStatus: mutation.status,
    participateInCanvass: mutation.mutate,
  };
}

export function useListPendingParticipants(
  canvass: Canvass,
  { enablePolling = false } = {},
) {
  const query = useQuery({
    queryKey: ["canvass", "pending-participants", canvass.id],
    queryFn: () => canvassesGateway.listPendingParticipants(canvass),
    refetchInterval: enablePolling ? POLL_INTERVAL_MS : false,
  });

  return {
    ...query,
    listPendingParticipantsStatus: query.status,
    pendingParticipants: query.data,
  };
}

export function useConfirmPendingParticipant(canvass: Canvass) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (user: User) =>
      canvassesGateway.confirmPendingParticipant(canvass, user),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["canvasses"] });
      queryClient.invalidateQueries({
        queryKey: ["canvasses", "assignable-users"],
      });
      queryClient.invalidateQueries({ queryKey: ["canvass", canvass.id] });
      queryClient.invalidateQueries({
        queryKey: ["canvass", "pending-participants", canvass.id],
      });
    },
  });

  return {
    ...mutation,
    confirmPendingParticipantStatus: mutation.status,
    confirmPendingParticipant: mutation.mutate,
  };
}

export function useDeletePendingParticipant(canvass: Canvass) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (user: User) =>
      canvassesGateway.deletePendingParticipant(canvass, user),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["canvasses"] });
      queryClient.invalidateQueries({
        queryKey: ["canvassStats", canvass.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["canvass", canvass.id],
      });
      queryClient.invalidateQueries({
        queryKey: ["canvass", "pending-participants", canvass.id],
      });
    },
  });

  return {
    ...mutation,
    deletePendingParticipantStatus: mutation.status,
    resetDeletePendingParticipant: mutation.reset,
    deletePendingParticipant: mutation.mutate,
  };
}
